// Demo email templates for the /email/template preview screen.
//
// These mirror the production HTML templates kept in the backend at
// sensordata-backend/src/controllers/templates/emailTemplates/. They use the
// same `#placeholder#` tokens, which are filled with the `sample` values below
// so the screen can render a realistic preview of each email.

export interface EmailTemplate {
  /** Stable id, matches the backend template filename (without .html). */
  id: string;
  /** Human-friendly name shown in the list. */
  name: string;
  /** Short description of when this email is sent. */
  description: string;
  /** Raw HTML with `#placeholder#` tokens. */
  html: string;
  /** Sample values used to fill the placeholders in the preview. */
  sample: Record<string, string>;
}

const BUTTON =
  "display:inline-block;padding:15px 36px;background:#1E40AF;color:#FFFFFF;" +
  "text-decoration:none;font-size:16px;font-weight:600;border-radius:4px;";

const PANEL = "background:#F8FAFC;border:1px solid #E5E7EB;";

/** Wrap body content in the shared OSMOAIR email shell. */
function shell(title: string, subtitle: string, body: string): string {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${title} - OSMOAIR</title>
</head>
<body style="margin:0;padding:0;background:#F6F8FA;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;color:#111827;">
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background:#F6F8FA;padding:40px 20px;">
<tr><td align="center">
<table width="640" cellpadding="0" cellspacing="0" border="0" style="background:#FFFFFF;border:1px solid #E5E7EB;">

  <tr>
    <td style="padding:40px 48px 24px 48px;">
      <div style="font-size:34px;font-weight:700;color:#1E40AF;line-height:1;">OSMOAIR</div>
      <div style="color:#6B7280;font-size:18px;margin-top:6px;">Monitoring Solutions</div>
      <div style="width:60px;height:2px;background:#1E40AF;margin-top:18px;"></div>
    </td>
  </tr>

  <tr><td style="padding:0 48px;"><div style="border-top:1px solid #E5E7EB;"></div></td></tr>

  <tr>
    <td style="padding:36px 48px 10px 48px;">
      <div style="font-size:28px;font-weight:600;color:#111827;">${title}</div>
      <div style="margin-top:6px;font-size:13px;color:#6B7280;">${subtitle}</div>
    </td>
  </tr>

  ${body}

  <tr>
    <td style="padding:40px 48px 48px 48px;">
      <div style="border-top:1px solid #E5E7EB;padding-top:24px;">
        <div style="font-size:14px;line-height:1.8;color:#6B7280;">
          Need assistance? Contact our support team at
          <a href="mailto:support@osmoair.com" style="color:#1E40AF;text-decoration:none;">support@osmoair.com</a>
        </div>
        <div style="margin-top:24px;font-size:14px;color:#111827;">
          Regards,<br><strong>OSMOAIR Team</strong>
        </div>
      </div>
    </td>
  </tr>

</table>
</td></tr>
</table>
</body>
</html>`;
}

const para = (html: string) =>
  `<tr><td style="padding:0 48px;font-size:15px;line-height:1.8;color:#4B5563;">${html}</td></tr>`;

const note = (html: string) =>
  `<tr><td style="padding:24px 48px 0 48px;font-size:14px;line-height:1.8;color:#4B5563;">${html}</td></tr>`;

const ALERT_CSS = `
:root{--primary:#1E40AF;--danger:#DC2626;--border:#E5E7EB;--surface:#F8FAFC;--muted:#6B7280;}
body{margin:0;padding:40px 20px;background:#F6F8FA;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif;color:#111827;}
*{box-sizing:border-box;}
.email-container{max-width:720px;margin:0 auto;background:#FFFFFF;border:1px solid var(--border);}
.header{padding:40px 48px 24px;}
.logo{font-size:32px;font-weight:700;color:var(--primary);}
.subtitle{font-size:18px;color:var(--muted);margin-top:4px;}
.accent{width:56px;height:2px;background:var(--primary);margin-top:18px;}
.accent-danger{background:var(--danger);}
.rule{border-top:1px solid var(--border);}
.escalation-banner{background:var(--danger);color:#FFFFFF;text-align:center;padding:10px 20px;font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;}
.title-row{padding:32px 48px 0;display:flex;justify-content:space-between;align-items:flex-start;}
.title h1{margin:0;font-size:28px;font-weight:600;color:var(--danger);}
.title p{margin:4px 0 0;color:var(--muted);font-size:13px;}
.meta{text-align:right;font-size:12px;line-height:1.8;color:var(--muted);font-family:Consolas,monospace;}
.message{padding:24px 48px 0;font-size:14px;line-height:1.8;color:#4B5563;}
.alert-box{margin:28px 48px 0;background:#FEF2F2;border-left:4px solid var(--danger);padding:18px 20px;font-size:14px;line-height:1.8;color:#7F1D1D;}
.reading-card{margin:28px 48px 0;border:1px solid var(--border);background:var(--surface);text-align:center;padding:36px;}
.reading-label{font-size:12px;text-transform:uppercase;letter-spacing:.08em;color:var(--muted);}
.reading-value{margin-top:12px;font-size:52px;font-weight:700;color:var(--danger);}
.reading-threshold{margin-top:12px;font-size:14px;color:var(--muted);}
.section-title{padding:32px 48px 14px;font-size:18px;font-weight:600;}
.asset-table{width:calc(100% - 96px);margin:0 48px;border-collapse:collapse;font-size:13px;}
.asset-table tbody td{padding:14px 12px;border-top:1px solid var(--border);}
.actions{margin:0 48px;background:#FFFBEB;border-left:4px solid #F59E0B;padding:18px 20px;font-size:14px;line-height:2;color:#4B5563;}
.cta{text-align:center;padding:32px 48px;}
.cta a{display:inline-block;background:var(--primary);color:#FFFFFF;text-decoration:none;padding:14px 28px;border-radius:6px;font-weight:600;}
.footer{margin:0 48px 40px;padding-top:20px;border-top:1px solid var(--border);font-size:12px;color:var(--muted);line-height:1.8;}
`;

function alertFooter(): string {
  return `<div class="footer">
    <table width="100%" cellpadding="0" cellspacing="0" border="0">
      <tr>
        <td align="left" style="font-size:12px;color:#6B7280;"><a href="mailto:support@osmoair.com" style="color:#6B7280;text-decoration:none;">support@osmoair.com</a></td>
        <td align="right" style="font-size:12px;color:#6B7280;"><a href="https://www.osmoair.com" style="color:#6B7280;text-decoration:none;">www.osmoair.com</a></td>
      </tr>
    </table>
    <div style="text-align:center;margin-top:16px;font-size:12px;color:#6B7280;">Alert Generated Automatically by OSMOAIR Monitoring Solutions</div>
  </div>`;
}

/** Build the full alert email HTML. `escalation=true` renders the secondary variant. */
function buildAlert(escalation: boolean): string {
  const header = escalation
    ? `<div class="escalation-banner">Escalated Alert &mdash; Condition Still Unresolved</div>`
    : `<div class="rule"></div>`;

  const titleSub = escalation
    ? "Threshold Still Exceeded &mdash; Escalation Notice"
    : "Monitored Threshold Exceeded";

  const metaLabel = escalation ? "LAST CHECKED" : "TRIGGERED AT";

  const message = escalation
    ? "Dear Facility Team,<br><br>A primary alert for <strong>#parameterName#</strong> at <strong>#locationName#</strong> was sent earlier, but the value has <strong>not</strong> returned to the acceptable range. This escalation notice requires your immediate attention."
    : "Dear Facility Team,<br><br>OSMOAIR has detected a reading outside the configured threshold for one of your monitored devices. Immediate review is recommended.";

  const alertBox = escalation
    ? "The <strong>#parameterName#</strong> reading remains outside its configured threshold and has not recovered since the first alert. Please address the issue immediately."
    : "The <strong>#parameterName#</strong> reading at <strong>#locationName#</strong> has crossed its configured threshold. Please review the conditions below.";

  const timeRows = escalation
    ? `<tr><td><strong>First Triggered</strong></td><td>#primaryTimestamp#</td></tr>
       <tr><td><strong>Last Checked</strong></td><td>#timestamp#</td></tr>`
    : `<tr><td><strong>Triggered At</strong></td><td>#timestamp#</td></tr>`;

  const actions = escalation
    ? "&bull; Treat this as a priority &mdash; the condition has persisted since the first alert<br>&bull; Inspect the affected device and its surrounding conditions on-site<br>&bull; Take immediate corrective action to bring the parameter back into range<br>&bull; Continue monitoring until values return to the normal range"
    : "&bull; Review the affected device and its surrounding conditions<br>&bull; Verify the reading against on-site instrumentation<br>&bull; Take corrective action to bring the parameter back into range<br>&bull; Continue monitoring until values return to the normal range";

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>OSMOAIR Alert Notification</title>
<style>${ALERT_CSS}</style>
</head>
<body>
<div class="email-container">
  <div class="header">
    <div class="logo">OSMOAIR</div>
    <div class="subtitle">Monitoring Solutions</div>
    <div class="accent ${escalation ? "accent-danger" : ""}"></div>
  </div>
  ${header}
  <div class="title-row">
    <div class="title">
      <h1>#parameterName# ALERT</h1>
      <p>${titleSub}</p>
    </div>
    <div class="meta">${metaLabel}<br>${escalation ? "#timestamp#" : "#timestamp#"}</div>
  </div>
  <div class="message">${message}</div>
  <div class="alert-box">${alertBox}</div>
  <div class="reading-card">
    <div class="reading-label">Current #parameterName# Reading</div>
    <div class="reading-value">#currentValue# #unit#</div>
    <div class="reading-threshold">Threshold Condition: <strong>#condition#</strong></div>
  </div>
  <div class="section-title">Device Information</div>
  <table class="asset-table"><tbody>
    <tr><td><strong>Device</strong></td><td>#deviceName#</td></tr>
    <tr><td><strong>Location</strong></td><td>#locationName#</td></tr>
    <tr><td><strong>Parameter</strong></td><td>#parameterName#</td></tr>
    <tr><td><strong>Current Value</strong></td><td><strong style="color:#DC2626;">#currentValue# #unit#</strong></td></tr>
    ${timeRows}
  </tbody></table>
  <div class="section-title">Recommended Actions</div>
  <div class="actions">${actions}</div>
  <div class="cta"><a href="https://app.osmoair.com/dashboard">View Live Dashboard</a></div>
  ${alertFooter()}
</div>
</body>
</html>`;
}

const DOC_CSS = `
:root{--primary:#1E40AF;--success:#15803D;--warning:#B45309;--danger:#DC2626;--border:#E5E7EB;--surface:#F8FAFC;--muted:#6B7280;}
body{margin:0;padding:40px 20px;background:#F6F8FA;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif;color:#111827;}
*{box-sizing:border-box;}
.email-container{max-width:720px;margin:0 auto;background:#FFFFFF;border:1px solid var(--border);}
.header{padding:40px 48px 24px;}
.logo{font-size:32px;font-weight:700;color:var(--primary);}
.subtitle{font-size:18px;color:var(--muted);margin-top:4px;}
.accent{width:56px;height:2px;background:var(--primary);margin-top:18px;}
.rule{border-top:1px solid var(--border);}
.title-row{padding:32px 48px 0;display:flex;justify-content:space-between;align-items:flex-start;}
.title h1{margin:0;font-size:28px;font-weight:600;}
.title p{margin:4px 0 0;font-size:13px;color:var(--muted);}
.meta{text-align:right;font-size:12px;color:var(--muted);line-height:1.8;font-family:Consolas,monospace;}
.message{padding:24px 48px 0;font-size:14px;line-height:1.8;color:#4B5563;}
.section-title{padding:32px 48px 14px;font-size:18px;font-weight:600;}
.overview{padding:0 48px;}
.overview-grid{display:flex;gap:12px;}
.overview-card{flex:1;border:1px solid var(--border);padding:16px;}
.overview-label{font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:var(--muted);}
.overview-value{margin-top:8px;font-size:20px;font-weight:600;color:var(--primary);}
.overview-note{margin-top:4px;font-size:12px;color:var(--muted);}
.asset-table{width:calc(100% - 96px);margin:0 48px;border-collapse:collapse;font-size:13px;}
.asset-table thead th{background:var(--surface);color:var(--primary);padding:12px;text-align:left;border-bottom:1px solid var(--border);}
.asset-table tbody td{padding:14px 12px;border-top:1px solid var(--border);}
.pass{color:var(--success);font-weight:600;}
.fail{color:var(--danger);font-weight:600;}
.nodata{color:var(--muted);font-weight:600;}
.cta{text-align:center;padding:32px 48px;}
.cta a{display:inline-block;background:var(--primary);color:#FFFFFF;text-decoration:none;padding:14px 28px;border-radius:6px;font-weight:600;}
.footer{margin:0 48px 40px;padding-top:20px;border-top:1px solid var(--border);}
`;

function docFooter(): string {
  return `<div class="footer">
    <table width="100%" cellpadding="0" cellspacing="0">
      <tr>
        <td align="left" style="font-size:12px;color:#6B7280;"><a href="mailto:support@osmoair.com" style="color:#6B7280;text-decoration:none;">support@osmoair.com</a></td>
        <td align="right" style="font-size:12px;color:#6B7280;"><a href="https://www.osmoair.com" style="color:#6B7280;text-decoration:none;">www.osmoair.com</a></td>
      </tr>
    </table>
    <div style="text-align:center;margin-top:16px;font-size:12px;color:#6B7280;">Report Generated Automatically by OSMOAIR Monitoring Solutions</div>
  </div>`;
}

const REPORT_HTML = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>OSMOAIR Sensor Data Report</title><style>${DOC_CSS}</style></head>
<body><div class="email-container">
  <div class="header"><div class="logo">OSMOAIR</div><div class="subtitle">Monitoring Solutions</div><div class="accent"></div></div>
  <div class="rule"></div>
  <div class="title-row"><div class="title"><h1>Report</h1><p>Data Monitoring Overview</p></div><div class="meta">GENERATED ON<br>#generatedOn#</div></div>
  <div class="message">Dear <strong>#organization#</strong>,<br><br>Please find below the environmental monitoring summary generated from the selected OSMOAIR device during the reporting period. The full dataset is included as <strong>#attachments#</strong> attached to this email.</div>
  <div class="section-title">Executive Summary</div>
  <div class="overview"><div class="overview-grid">
    <div class="overview-card"><div class="overview-label">Records</div><div class="overview-value">#recordCount#</div></div>
    <div class="overview-card"><div class="overview-label">Parameters</div><div class="overview-value">#parameterCount#</div></div>
    <div class="overview-card"><div class="overview-label">Period</div><div class="overview-value">#days# Days</div></div>
  </div></div>
  <div class="section-title">Report Information</div>
  <table class="asset-table"><tbody>
    <tr><td><strong>Organization</strong></td><td>#organization#</td></tr>
    <tr><td><strong>Device</strong></td><td>#deviceName#</td></tr>
    <tr><td><strong>Location</strong></td><td>#locationName#</td></tr>
    <tr><td><strong>Reporting Period</strong></td><td>#fromDate# &ndash; #toDate#</td></tr>
    <tr><td><strong>Generated On</strong></td><td>#generatedOn#</td></tr>
    <tr><td><strong>Attachments</strong></td><td>#attachments#</td></tr>
  </tbody></table>
  <div class="section-title">Environmental Parameters Summary</div>
  <table class="asset-table"><thead><tr><th>Parameter</th><th>Minimum</th><th>Average</th><th>Maximum</th></tr></thead><tbody>#parameterRows#</tbody></table>
  <div class="cta"><a href="https://app.osmoair.com/dashboard">View Dashboard</a></div>
  ${docFooter()}
</div></body></html>`;

const COMPLIANCE_HTML = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>OSMOAIR Compliance Assessment Report</title><style>${DOC_CSS}</style></head>
<body><div class="email-container">
  <div class="header"><div class="logo">OSMOAIR</div><div class="subtitle">Monitoring Solutions</div><div class="accent"></div></div>
  <div class="rule"></div>
  <div class="title-row"><div class="title"><h1>Compliance Assessment Report</h1><p>#profileName#</p></div><div class="meta">GENERATED ON<br>#generatedOn#</div></div>
  <div class="message">Dear <strong>#organization#</strong>,<br><br>Please find below the compliance assessment generated using the <strong>#profileName#</strong> profile and the environmental monitoring data collected during the reporting period. The full report is attached as a PDF.</div>
  <div class="section-title">Assessment Summary</div>
  <div class="overview"><div class="overview-grid">
    <div class="overview-card"><div class="overview-label">Overall Compliance</div><div class="overview-value">#overallCompliance#%</div><div class="overview-note">Across all rules</div></div>
    <div class="overview-card"><div class="overview-label">Rules Passed</div><div class="overview-value" style="color:#15803D;">#passedRules#</div><div class="overview-note">of #rulesEvaluated# evaluated</div></div>
    <div class="overview-card"><div class="overview-label">Violations</div><div class="overview-value" style="color:#DC2626;">#totalViolations#</div><div class="overview-note">Data points out of range</div></div>
  </div></div>
  <div class="section-title">Compliance Profile</div>
  <table class="asset-table"><tbody>
    <tr><td><strong>Profile Name</strong></td><td>#profileName#</td></tr>
    <tr><td><strong>Description</strong></td><td>#profileDescription#</td></tr>
    <tr><td><strong>Organization</strong></td><td>#organization#</td></tr>
    <tr><td><strong>Evaluation Period</strong></td><td>#fromDate# &ndash; #toDate# (#days# days)</td></tr>
    <tr><td><strong>Parameter Score</strong></td><td>#parameterScore#</td></tr>
  </tbody></table>
  <div class="section-title">Rule Assessment Results</div>
  <table class="asset-table"><thead><tr><th>Parameter</th><th>Configured Rule</th><th>Observed (Avg)</th><th>Result</th></tr></thead><tbody>#ruleRows#</tbody></table>
  <div class="cta"><a href="https://app.osmoair.com/compliance">View Dashboard</a></div>
  ${docFooter()}
</div></body></html>`;

export const EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: "email-otp",
    name: "Email OTP",
    description: "Sent during signup to verify the user's email with a one-time code.",
    sample: { name: "Keval", otp: "840843" },
    html: shell(
      "Verify Your Email Address",
      "Account Verification Required",
      [
        para(
          "Dear #name#,<br><br>Thank you for signing up with <strong>OSMOAIR</strong>. " +
            "Please use the verification code below to confirm your email address and complete your account setup."
        ),
        `<tr><td style="padding:32px 48px;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="${PANEL}">
            <tr><td align="center" style="padding:40px 20px;">
              <div style="font-size:14px;color:#6B7280;margin-bottom:20px;">Verification Code</div>
              <div style="font-size:44px;font-weight:700;letter-spacing:14px;color:#1E293B;font-family:Consolas,monospace;">#otp#</div>
            </td></tr>
          </table>
        </td></tr>`,
        note(
          "<strong style=\"color:#111827;\">This verification code will expire in 10 minutes.</strong>" +
            "<br><br>If you did not create an account with OSMOAIR, you may safely ignore this email."
        ),
      ].join("\n")
    ),
  },
  {
    id: "email-verification",
    name: "Email Verification (Link)",
    description: "Verifies a new email address through a confirmation link.",
    sample: { name: "Keval", verificationUrl: "https://app.osmoair.com/verify-email?token=demo-token-123" },
    html: shell(
      "Verify Your Email Address",
      "Account Verification Required",
      [
        para(
          "Dear #name#,<br><br>Thank you for signing up with <strong>OSMOAIR</strong>. " +
            "To complete your account setup, please verify your email address by clicking the button below."
        ),
        `<tr><td style="padding:32px 48px;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="${PANEL}">
            <tr><td align="center" style="padding:40px 20px;">
              <a href="#verificationUrl#" style="${BUTTON}">Verify Email Address</a>
            </td></tr>
          </table>
        </td></tr>`,
        para(
          "If the button above doesn't work, copy and paste the following link into your browser:<br>" +
            "<span style=\"display:inline-block;margin-top:10px;font-family:Consolas,monospace;font-size:13px;color:#1E40AF;word-break:break-all;\">#verificationUrl#</span>"
        ),
        note(
          "<strong style=\"color:#111827;\">This verification link will expire in 24 hours.</strong>" +
            "<br><br>If you did not create an account with OSMOAIR, you may safely ignore this email."
        ),
      ].join("\n")
    ),
  },
  {
    id: "forgot-password",
    name: "Forgot Password",
    description: "Sent when a user requests a password reset link.",
    sample: { name: "Keval", ResetPasswordLink: "https://app.osmoair.com/changePassword?token=demo-reset-456" },
    html: shell(
      "Reset Your Password",
      "Password Reset Request",
      [
        para(
          "Dear #name#,<br><br>We received a request to reset the password for your <strong>OSMOAIR</strong> account. " +
            "Click the button below to choose a new password. If you did not make this request, you may safely ignore this email."
        ),
        `<tr><td style="padding:32px 48px;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="${PANEL}">
            <tr><td align="center" style="padding:40px 20px;">
              <a href="#ResetPasswordLink#" target="_blank" style="${BUTTON}">Reset Your Password</a>
            </td></tr>
          </table>
        </td></tr>`,
        note(
          "<strong style=\"color:#111827;\">For your protection, this link will expire in 24 hours.</strong>" +
            "<br><br>If you didn't request a password reset, please contact our support team immediately at " +
            "<a href=\"mailto:support@osmoair.com\" style=\"color:#1E40AF;text-decoration:none;\">support@osmoair.com</a>."
        ),
      ].join("\n")
    ),
  },
  {
    id: "member-added",
    name: "Member Added",
    description: "Notifies an existing user that they were added to an organization.",
    sample: {
      name: "Keval",
      organizationName: "Acme Industries",
      email: "keval@acme.com",
      url: "https://app.osmoair.com/dashboard",
    },
    html: shell(
      "You've Been Added to an Organization",
      "Organization Membership",
      [
        para(
          "Dear #name#,<br><br>You have been added to the organization <strong>#organizationName#</strong> on OSMOAIR. " +
            "You can log in with your existing credentials to access the organization's dashboard and resources."
        ),
        `<tr><td style="padding:32px 48px;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="${PANEL}">
            <tr><td style="padding:28px 32px;font-size:15px;line-height:2;color:#4B5563;">
              <strong style="color:#111827;">Organization:</strong> #organizationName#<br>
              <strong style="color:#111827;">Your Email:</strong> #email#
            </td></tr>
          </table>
        </td></tr>`,
        `<tr><td align="center" style="padding:0 48px 8px 48px;">
          <a href="#url#" style="${BUTTON}">Go to Dashboard</a>
        </td></tr>`,
      ].join("\n")
    ),
  },
  {
    id: "credentials-generated",
    name: "Credentials Generated",
    description: "Delivers initial login credentials to a newly created user.",
    sample: {
      name: "Keval",
      email: "keval@acme.com",
      password: "Tmp#9fK2qX",
      url: "https://app.osmoair.com/login",
    },
    html: shell(
      "Welcome to OSMOAIR",
      "Your Account Login Credentials",
      [
        para(
          "Dear #name#,<br><br>Your account has been successfully created. " +
            "Use the login credentials below to access your <strong>OSMOAIR</strong> account."
        ),
        `<tr><td style="padding:32px 48px;">
          <table width="100%" cellpadding="0" cellspacing="0" border="0" style="${PANEL}">
            <tr><td style="padding:28px 32px;font-size:15px;line-height:2;color:#4B5563;">
              <strong style="color:#111827;">Email:</strong> #email#<br>
              <strong style="color:#111827;">Password:</strong> <span style="font-family:Consolas,monospace;color:#1E293B;">#password#</span>
            </td></tr>
          </table>
        </td></tr>`,
        `<tr><td align="center" style="padding:0 48px 8px 48px;">
          <a href="#url#" style="${BUTTON}">Login to Your Account</a>
        </td></tr>`,
        note(
          "<strong style=\"color:#111827;\">Security tip:</strong> For your protection, " +
            "we strongly recommend changing your password after your first login."
        ),
      ].join("\n")
    ),
  },
  {
    id: "primary-alert",
    name: "Primary Alert",
    description: "Sent when a device parameter first crosses its configured threshold.",
    sample: {
      parameterName: "CO₂",
      locationName: "Conference Room - Floor 2",
      deviceName: "OSMO-PRO-024",
      currentValue: "1284",
      unit: "ppm",
      condition: "> 1000 ppm",
      timestamp: "17 Jun 2026 14:32 IST",
    },
    html: buildAlert(false),
  },
  {
    id: "secondary-alert",
    name: "Secondary Alert (Escalation)",
    description: "Escalation sent when a threshold breach has not recovered after the cooldown.",
    sample: {
      parameterName: "CO₂",
      locationName: "Conference Room - Floor 2",
      deviceName: "OSMO-PRO-024",
      currentValue: "1284",
      unit: "ppm",
      condition: "> 1000 ppm",
      primaryTimestamp: "17 Jun 2026 14:14 IST",
      timestamp: "17 Jun 2026 14:32 IST",
    },
    html: buildAlert(true),
  },
  {
    id: "report",
    name: "Sensor Data Report",
    description: "Emailed when a device data report is generated, with Excel/PDF attachments.",
    sample: {
      organization: "Gravity India Technologies",
      deviceName: "OSMO-PRO-024",
      locationName: "Conference Room - Floor 2",
      fromDate: "Jun 16, 2026",
      toDate: "Jun 17, 2026",
      generatedOn: "17 Jun 2026, 08:00",
      recordCount: "11,520",
      parameterCount: "5",
      days: "1",
      attachments: "Excel (.xlsx) + PDF (.pdf)",
      parameterRows: [
        "<tr><td>CO₂</td><td>462 ppm</td><td>781 ppm</td><td>1284 ppm</td></tr>",
        "<tr><td>Temperature</td><td>23.8 °C</td><td>26.4 °C</td><td>29.2 °C</td></tr>",
        "<tr><td>Humidity</td><td>51 %</td><td>63 %</td><td>71 %</td></tr>",
        "<tr><td>PM2.5</td><td>12 µg/m³</td><td>24 µg/m³</td><td>38 µg/m³</td></tr>",
        "<tr><td>TVOC</td><td>102 ppb</td><td>278 ppb</td><td>412 ppb</td></tr>",
      ].join(""),
    },
    html: REPORT_HTML,
  },
  {
    id: "compliance-report",
    name: "Compliance Report",
    description: "Emailed compliance assessment for a profile, with the full PDF attached.",
    sample: {
      profileName: "Corporate IAQ Policy 2026",
      profileDescription: "Indoor air-quality limits for office spaces",
      organization: "Gravity India Technologies",
      fromDate: "May 18, 2026",
      toDate: "Jun 17, 2026",
      days: "30",
      generatedOn: "17 Jun 2026, 08:00",
      overallCompliance: "92",
      parameterScore: "4/5",
      rulesEvaluated: "5",
      passedRules: "4",
      failedRules: "1",
      totalViolations: "318",
      ruleRows: [
        '<tr><td>CO₂</td><td>≤ 1000 ppm</td><td>781 ppm</td><td class="pass">Compliant</td></tr>',
        '<tr><td>PM2.5</td><td>≤ 35 µg/m³</td><td>24 µg/m³</td><td class="pass">Compliant</td></tr>',
        '<tr><td>Temperature</td><td>22 - 30 °C</td><td>26.4 °C</td><td class="pass">Compliant</td></tr>',
        '<tr><td>TVOC</td><td>≤ 500 ppb</td><td>278 ppb</td><td class="pass">Compliant</td></tr>',
        '<tr><td>Humidity</td><td>40 - 70 %</td><td>72 %</td><td class="fail">Non-Compliant</td></tr>',
      ].join(""),
    },
    html: COMPLIANCE_HTML,
  },
];

/** Replace every `#token#` in the template with its sample value. */
export function renderTemplate(template: EmailTemplate): string {
  let html = template.html;
  for (const [key, value] of Object.entries(template.sample)) {
    html = html.replace(new RegExp(`#${key}#`, "g"), value);
  }
  return html;
}

/** Tokens detected in a template, in order of first appearance. */
export function templateVariables(template: EmailTemplate): string[] {
  const matches = template.html.match(/#([a-zA-Z]+)#/g) ?? [];
  return Array.from(new Set(matches.map((m) => m.slice(1, -1))));
}
